import { Box, Chip, ClickAwayListener, Popper } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { MouseEvent, useRef, useState } from "react";

const BRAND_GREEN = "#014339";
const MUTED = "#6b7a6b";

const DELAY_OPTIONS = [
  { label: "1 day", value: 1 },
  { label: "2 days", value: 2 },
  { label: "3 days", value: 3 },
  { label: "4 days", value: 4 },
  { label: "5 days", value: 5 },
  { label: "7 days", value: 7 },
  { label: "10 days", value: 10 },
  { label: "14 days", value: 14 },
];

interface FollowUpTabProps {
  tabIndex: number;
  days: number;
  sx?: SxProps<Theme>;
  onSelect: (e: MouseEvent) => void;
  onDelayChange: (value: number) => void;
  onRemove: () => void;
}

export default function FollowUpTab({
  tabIndex,
  days,
  sx,
  onSelect,
  onDelayChange,
  onRemove,
}: FollowUpTabProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const label = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      <span>{`Follow-Up ${tabIndex}`}</span>
      <Box
        component="span"
        ref={anchorRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          ml: 0.75,
          pl: 0.75,
          borderLeft: "1px solid rgba(0,0,0,0.12)",
          fontSize: 11,
          color: MUTED,
          cursor: "pointer",
          whiteSpace: "nowrap",
          "&:hover": { color: BRAND_GREEN },
        }}
      >
        {`${days}d`}
        <svg width="7" height="7" viewBox="0 0 12 12" fill="currentColor">
          <path
            d="M3 5l3 3 3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </Box>
  );

  return (
    <>
      <Chip
        label={label}
        size="small"
        sx={[{ flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
        onClick={onSelect}
        onDelete={onRemove}
        deleteIcon={<Box component="i" className="fa-thin fa-xmark" />}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom"
        disablePortal={false}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              background: "#fff",
              border: "1px solid #e0e2e0",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              minWidth: 160,
              padding: "4px 0",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                pt: 0.75,
                pb: 0.75,
                mb: 0.5,
                borderBottom: "1px solid #f0f2f0",
                fontSize: 10,
                lineHeight: 1.3,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: MUTED,
              }}
            >
              Send follow-up after
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: 10,
                  fontWeight: 400,
                  letterSpacing: 0,
                  textTransform: "none",
                  color: "#9aa39a",
                  mt: "1px",
                }}
              >
                Counted in business days
              </Box>
            </Box>
            {DELAY_OPTIONS.map((opt) => {
              const selected = opt.value === days;
              return (
                <Box
                  key={opt.value}
                  component="button"
                  type="button"
                  onClick={() => {
                    onDelayChange(opt.value);
                    setOpen(false);
                  }}
                  sx={{
                    display: "block",
                    width: "100%",
                    padding: "6px 12px",
                    background: selected ? "#f0f4f0" : "transparent",
                    border: "none",
                    textAlign: "left",
                    fontSize: 12,
                    color: selected ? BRAND_GREEN : "#3a3a3a",
                    fontWeight: selected ? 600 : 400,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    "&:hover": {
                      background: selected ? "#f0f4f0" : "#f7f9f7",
                    },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
